import { useKonvaConstructionAnimation } from "../animation/useKonvaConstructionAnimation";
import Konva from 'konva';
import React from 'react';
import { Group, Layer, Rect, Stage } from 'react-konva';
import { Grid } from '../../Grid';
import { Scrollbars } from '../../Scrollbars';
import { useMapCanvas, useMapCanvasInteraction } from '../contexts/MapCanvasContext';
import { useMapEditorSelection, useMapEditorUI } from '../contexts/MapEditorContext';
import {
  SELECTION_TOOL_FILL_COLOR,
  SELECTION_TOOL_STROKE_COLOR
} from '../defines';
import { SpotLineDrawing } from '../editor/spot-numbering/SpotLineDrawing';
import { useMapEditorHandlers } from '../hooks/useMapEditorHandlers';
import { ShapeTransformer } from '../shapes/ShapeTransformer';
import { MapCanvasPreviewShape } from './MapCanvasPreviewShape';
import { MapCanvasViewLayers } from './MapCanvasViewLayers';
import { MapCanvasEditingAreaLayer } from './layers/MapCanvasEditingAreaLayer';
import { useAddDoorShortcut } from '../editor/shortcuts/hooks/useAddDoorShortcut';
import { useAddWindowShortcut } from '../editor/shortcuts/hooks/useAddWindowShortcut';
import { useWallBridgeShortcut } from '../editor/shortcuts/hooks/useWallBridgeShortcut';
import { useRemoveDoorShortcut } from '../editor/shortcuts/hooks/useRemoveDoorShortcut';
import { useRemoveWindowShortcut } from '../editor/shortcuts/hooks/useRemoveWindowShortcut';
import { useWallExtendShortcut } from '../editor/shortcuts/hooks/useWallExtendShortcut';
import { MapCanvasGhostLayers } from './layers/MapCanvasGhostLayers';
import { AutoZoneOverlay } from './layers/AutoZoneOverlay';
import { GuidePointOverlay } from './layers/GuidePointOverlay';
import { ZoneChecksOverlay } from './layers/ZoneChecksOverlay';
import { useGetEditorShapesByType } from '../hooks/useGetEditorShapesByType';
import { MeasurementPrompt } from '../editor/toolbars/MeasurementPrompt';
import { GridPrompt } from '../editor/toolbars/GridPrompt';

interface MapEditorCanvasProps {
  stagePosition: { x: number; y: number; scale: number };
  onStageChange: (position: { x: number; y: number; scale: number }) => void;
}

/** Isolated component that subscribes to selection context.
 *  Only this component re-renders when selectedIds changes — not the entire Stage. */
const SelectionTransformer = React.memo<{
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  transformerRef: React.MutableRefObject<Konva.Transformer | null>;
  selectedIdsRef: React.MutableRefObject<string[]>;
}>(({ stageRef, transformerRef, selectedIdsRef }) => {
  const { selectedIds } = useMapEditorSelection();

  // Sync ref so useMapEditorHandlers can read latest selectedIds without subscribing
  selectedIdsRef.current = selectedIds;

  React.useEffect(() => {
    if (transformerRef.current && selectedIds.length > 1) {
      const stage = stageRef.current;
      if (!stage) return;

      const nodes = selectedIds
        .map((id) => stage.findOne(`#shape-${id}`))
        .filter(Boolean);

      transformerRef.current.nodes(nodes as never);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedIds, stageRef, transformerRef]);

  if (selectedIds.length <= 1) return null;
  return <ShapeTransformer ref={transformerRef} />;
});

SelectionTransformer.displayName = "SelectionTransformer";

/** Isolates shape rendering from MapEditorCanvas.
 *  Subscribes to editor contexts internally so MapEditorCanvas doesn't re-render on selection/tool changes. */
const MapEditorViewLayers = React.memo<{
  shapesLayerOverlay?: React.ReactNode;
}>(({ shapesLayerOverlay }) => {
  const { showOutside } = useMapEditorUI();
  const getShapes = useGetEditorShapesByType();
  return (
    <MapCanvasViewLayers
      showOutside={showOutside}
      getShapes={getShapes}
      shapesLayerOverlay={shapesLayerOverlay}
    />
  );
});

MapEditorViewLayers.displayName = "MapEditorViewLayers";

const classes = {
  Wrapper: 'relative w-full h-full'
};

export const MapEditorCanvas: React.FC<MapEditorCanvasProps> = ({
  stagePosition,
  onStageChange
}) => {
  const iconToolGhostRef = React.useRef<Konva.Group>(null);
  const stageRef = React.useRef<Konva.Stage>(null);
  useKonvaConstructionAnimation(stageRef);
  const transformerRef = React.useRef<Konva.Transformer>(null);
  const selectedIdsRef = React.useRef<string[]>([]);

  const { showGrid } = useMapEditorUI();
  const {
    width,
    height,
    editingZoneWidth,
    editingZoneHeight
  } = useMapCanvas();
  const { selectionBox, isDragging } = useMapCanvasInteraction();

  const {
    handleMultiDragStart,
    handleMultiDragMove,
    handleMultiDragEnd,
    isPanToolActive,
    isMouseIn,
    isDrawing,
    drawingPoints,
    cursor,
    pendingMeasurement,
    confirmMeasurement,
    cancelMeasurement,
    pendingGrid,
    confirmGrid,
    cancelGrid,
    ...handlers
  } = useMapEditorHandlers({
    iconToolGhostRef,
    stageRef,
    transformerRef,
    selectedIdsRef
  });

  useWallBridgeShortcut(stageRef);
  useAddDoorShortcut(stageRef);
  useAddWindowShortcut(stageRef);
  useRemoveDoorShortcut(stageRef);
  useRemoveWindowShortcut(stageRef);
  useWallExtendShortcut(stageRef);

  return (
    <div className={classes.Wrapper}>
      <Stage
        style={{ cursor, backgroundColor: '#F3F4F6' }}
        ref={stageRef}
        width={width}
        height={height}
        scaleX={stagePosition.scale}
        scaleY={stagePosition.scale}
        x={stagePosition.x}
        y={stagePosition.y}
        draggable={isPanToolActive}
        {...handlers}
      >
        <MapCanvasEditingAreaLayer />
        <MapEditorViewLayers
          shapesLayerOverlay={
            <SelectionTransformer
              stageRef={stageRef}
              transformerRef={transformerRef}
              selectedIdsRef={selectedIdsRef}
            />
          }
        />
        <Layer>
          <Group>
            <SpotLineDrawing
              isDrawing={isDrawing}
              points={drawingPoints}
              onDrawEnd={() => {}}
            />
          </Group>
          <Group>
            {isDragging && selectionBox && (
              <Rect
                x={selectionBox.x}
                y={selectionBox.y}
                width={selectionBox.width}
                height={selectionBox.height}
                stroke={SELECTION_TOOL_STROKE_COLOR}
                strokeWidth={1}
                fill={SELECTION_TOOL_FILL_COLOR}
              />
            )}
          </Group>
          <MapCanvasPreviewShape />
          <AutoZoneOverlay />
          <ZoneChecksOverlay />
          <GuidePointOverlay />
          <MapCanvasGhostLayers
            iconToolGhostRef={iconToolGhostRef}
            isMouseIn={isMouseIn}
          />
          {showGrid && <Grid />}
        </Layer>
      </Stage>
      <Scrollbars
        width={width}
        height={height}
        contentWidth={editingZoneWidth}
        contentHeight={editingZoneHeight}
        scrollX={stagePosition.x}
        scrollY={stagePosition.y}
        scale={stagePosition.scale}
        onScroll={(x, y) => onStageChange({ ...stagePosition, x, y })}
      />
      {pendingMeasurement && (
        <MeasurementPrompt
          pixelLength={pendingMeasurement.pixelLength}
          onConfirm={confirmMeasurement}
          onCancel={cancelMeasurement}
        />
      )}
      {pendingGrid && (
        <GridPrompt
          rect={{ width: pendingGrid.width, height: pendingGrid.height }}
          onConfirm={confirmGrid}
          onCancel={cancelGrid}
        />
      )}
    </div>
  );
};
